import { OneDataCollection } from '@factorialco/factorial-one-react/dist/experimental'
import { MeetingsAllMeetingsTableDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { formatDateToString, formatMeetingDuration } from 'modules/meetings/lib/utils/dates'
import { getMeetingTypeIcon, getMeetingTypeTitle } from 'modules/meetings/lib/utils/meetings'

interface AllMeetingsTableProps {
  setOpenCreateScheduledMeeting: (open: boolean) => void
}

export const AllMeetingsTable = ({ setOpenCreateScheduledMeeting }: AllMeetingsTableProps) => {
  const { navigation } = useNavigation()
  const {
    access: { locale },
  } = useCurrentSession()

  const source = useGraphqlDataSource({
    document: MeetingsAllMeetingsTableDocument,
    mapData: (data) => data.meetings.meetingsConnection.nodes,
    mapPagination: (data) => ({
      total: data.meetings.meetingsConnection.totalCount ?? 0,
    }),
    mapVariables: ({ sortings, search }) => {
      return {
        sort: sortings?.[0],
        search,
      }
    },
    sortings: {
      title: {
        label: i18n.t('meetings.all.table.title'),
        sortBy: 'title',
      },
      date: {
        label: i18n.t('meetings.all.table.date'),
        sortBy: 'date',
      },
      meetingType: {
        label: i18n.t('meetings.all.table.type'),
        sortBy: 'meetingType',
      },
    },
    filters: {
      meetingType: {
        label: i18n.t('meetings.all.table.type'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t('meetings.meeting.type.one_on_one'),
              value: 'one_on_one',
            },
          ],
        },
      },
    },
    defaultSorting: {
      field: 'date',
      order: 'desc',
    },
    search: {
      enabled: true,
    },
    primaryActions: () => ({
      label: i18n.t('meetings.all.table.create_scheduled_meeting_title'),
      icon: undefined,
      onClick: () => setOpenCreateScheduledMeeting(true),
    }),
    itemUrl: ({ id }) =>
      id ? navigation.engagement.one_on_ones.meeting.meetId.fullPath({ id }) : undefined,
  })

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('meetings.all.table.title'),
                render: ({ title }) => title ?? '-',
                sorting: 'title',
              },
              {
                label: i18n.t('meetings.all.table.organizer'),
                render: ({ organizer }) => ({
                  type: 'person',
                  value: {
                    firstName: organizer?.firstName ?? '',
                    lastName: organizer?.lastName ?? '',
                    src: buildUrl(organizer?.avatar?.url),
                  },
                }),
              },
              {
                label: i18n.t('meetings.all.table.participants'),
                render: ({ attendees, organizer }) => {
                  const attendeesList = attendees
                    .filter((attendee) => attendee.employee?.id !== organizer?.id)
                    .map((attendee) => ({
                      type: 'person',
                      firstName: attendee.employee?.firstName ?? '',
                      lastName: attendee.employee?.lastName ?? '',
                      src: buildUrl(attendee.employee?.avatar?.url),
                    }))

                  if (attendeesList.length === 0) {
                    return {
                      type: 'text',
                      value: '-',
                    }
                  }

                  if (attendeesList.length === 1) {
                    return {
                      type: 'person',
                      value: {
                        firstName: attendeesList[0]?.firstName ?? '',
                        lastName: attendeesList[0]?.lastName ?? '',
                        src: buildUrl(attendeesList[0]?.src),
                      },
                    }
                  }

                  return {
                    type: 'avatarList',
                    value: {
                      avatarList: attendeesList,
                      max: 3,
                    },
                  }
                },
              },
              {
                label: i18n.t('meetings.all.table.date'),
                render: ({ date }) => formatDateToString(date, locale),
                sorting: 'date',
              },
              {
                label: i18n.t('meetings.all.table.time'),
                render: ({ startTime, endTime }) =>
                  formatMeetingDuration(startTime, endTime, locale),
              },
              {
                label: i18n.t('meetings.all.table.type'),
                render: ({ meetingType }) => ({
                  type: 'tag',
                  value: {
                    label: getMeetingTypeTitle(meetingType ?? 'default'),
                    icon: getMeetingTypeIcon(meetingType ?? 'default'),
                  },
                }),
                sorting: 'meetingType',
              },
            ],
          },
        },
      ]}
    />
  )
}
